import axios from 'axios';

let API_URL = process.env.VUE_APP_API_BASE_URL || 'http://localhost:3003/api';

// Fallback to append /api if it was mistakenly omitted in .env
if (API_URL && !API_URL.endsWith('/api') && API_URL.includes('localhost:3003')) {
  API_URL = `${API_URL.replace(/\/$/, '')}/api`;
}

const DEEPCOREX_URL = process.env.VUE_APP_DEEPCOREX_URL || 'http://localhost:5101';

const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor for API calls
axiosInstance.interceptors.request.use(
  async config => {
    if (typeof window !== 'undefined') window.dispatchEvent(new Event('api:start'));
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    if (typeof window !== 'undefined') window.dispatchEvent(new Event('api:end'));
    Promise.reject(error);
  }
);

// Response interceptor for API calls
axiosInstance.interceptors.response.use((response) => {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('api:end'));
  return response;
}, async function (error) {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('api:end'));
  const originalRequest = error.config;
  if (error.response?.status === 401 && !originalRequest._retry) {
    // Handle token expiration or unauthorized access
    localStorage.removeItem('access_token');
    
    // Redirect to DeepCoreX login
    if (window.location.pathname !== '/login' && window.location.pathname !== '/auth/callback') {
        window.location.href = `${DEEPCOREX_URL}/login?redirect=deeptrack`;
    }
    return Promise.reject(error);
  }
  return Promise.reject(error);
});

export const api = {
  auth: {
    login: async (credentials) => {
      const response = await axiosInstance.post('/auth/login', credentials);
      if (response.data.session?.access_token) {
        localStorage.setItem('access_token', response.data.session.access_token);
      }
      return response.data;
    },
    signup: async (userData) => {
      const response = await axiosInstance.post('/auth/signup', userData);
      const token = response.data.token || response.data.session?.access_token;
      if (token) {
        localStorage.setItem('access_token', token);
      }
      return response.data;
    },
    getMe: async () => {
        try {
            const response = await axiosInstance.get('/auth/me');
            return response.data;
        } catch (e) {
            return null;
        }
    },
    logout: async () => {
      localStorage.removeItem('access_token');
      return Promise.resolve();
    },
    resetPassword: async () => {
        // Implement if backend supports it
        // return axiosInstance.post('/auth/reset-password', { email });
        console.warn('Reset password not fully implemented in backend');
        return { error: null }; 
    },
    updateUser: async (data) => {
        // Implement if backend supports it
         return axiosInstance.patch('/auth/me', data);
    }
  },
  data: {
    list: async (resource, params = {}) => {
      const response = await axiosInstance.get(`/data/${resource}`, { params });
      return response.data;
    },
    get: async (resource, id) => {
      const response = await axiosInstance.get(`/data/${resource}/${id}`);
      return response.data;
    },
    create: async (resource, data) => {
      const response = await axiosInstance.post(`/data/${resource}`, data);
      return response.data;
    },
    update: async (resource, id, data) => {
      const response = await axiosInstance.patch(`/data/${resource}/${id}`, data);
      return response.data;
    },
    delete: async (resource, id) => {
      const response = await axiosInstance.delete(`/data/${resource}/${id}`);
      return response.data;
    }
  },
  storage: {
    // Direct-to-R2 upload via a server-signed PUT URL. Avoids the Cloud Run
    // request-body limit that broke large multipart POSTs through multer.
    upload: async (bucket, file, filePath = null) => {
      const path = filePath || file.name;
      const { data: sig } = await axiosInstance.post('/storage/sign-upload', { bucket, filePath: path });
      await axios.put(sig.uploadUrl, file, {
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        transformRequest: [(d) => d],
      });
      return { path: sig.path, fullPath: sig.path, url: sig.path };
    },
    getPublicUrl: (bucket, path) => {
        // Helper to construct URL
        const cleanPath = path.startsWith('/') ? path.slice(1) : path;
        const clientId = process.env.VUE_APP_DEFAULT_CLIENT_ID || 'default';
        return `${API_URL.replace('/api', '')}/uploads/${clientId}/${bucket}/${cleanPath}`;
    },
    remove: async (bucket, paths) => {
        const clientId = process.env.VUE_APP_DEFAULT_CLIENT_ID || 'default';
        const response = await axiosInstance.post(`/storage/${clientId}/${bucket}/remove`, paths);
        return response.data;
    },
    download: async (bucket, path) => {
         const clientId = process.env.VUE_APP_DEFAULT_CLIENT_ID || 'default';
         const response = await axiosInstance.get(`/storage/${clientId}/${bucket}/${path}`, { responseType: 'blob' });
         return response.data;
    }
  },
  functions: {
    invoke: async (functionName, body) => {
      const response = await axiosInstance.post(`/functions/${functionName}`, body);
      return response.data;
    }
  },
  // Procurement business-logic endpoints (GRN, 4-way match, payment approval)
  procurement: {
    createGRN: async (data) => (await axiosInstance.post('/procurement/grn', data)).data,
    verifyDO: async (id) => (await axiosInstance.post(`/procurement/do/${id}/verify`)).data,
    rejectDO: async (id, reason) => (await axiosInstance.post(`/procurement/do/${id}/reject`, { reason })).data,
    requestPayment: async (data) => (await axiosInstance.post('/procurement/request-payment', data)).data,
    listPayments: async () => (await axiosInstance.get('/procurement/payments')).data,
    myPendingPayments: async () => (await axiosInstance.get('/procurement/payments/my-pending')).data,
    getPayment: async (id) => (await axiosInstance.get(`/procurement/payment/${id}`)).data,
    approvePayment: async (id) => (await axiosInstance.post(`/procurement/payment/${id}/approve`)).data,
    rejectPayment: async (id, reason) => (await axiosInstance.post(`/procurement/payment/${id}/reject`, { reason })).data,
    tracking: async (prId) => (await axiosInstance.get(`/procurement/pr/${prId}/tracking`)).data,
  },
  // Finance-only cost allocation endpoints (confidential employee rates,
  // HQ recharge percentages, master-data bootstrap)
  finance: {
    listEmployeeCosts: async () => (await axiosInstance.get('/finance/employee-costs')).data,
    createEmployeeCost: async (data) => (await axiosInstance.post('/finance/employee-costs', data)).data,
    updateEmployeeCost: async (id, data) => (await axiosInstance.patch(`/finance/employee-costs/${id}`, data)).data,
    deleteEmployeeCost: async (id) => (await axiosInstance.delete(`/finance/employee-costs/${id}`)).data,
    getHqPercentages: async () => (await axiosInstance.get('/finance/hq-percentages')).data,
    saveHqPercentages: async (data) => (await axiosInstance.post('/finance/hq-percentages', data)).data,
    bootstrapDefaults: async () => (await axiosInstance.post('/finance/bootstrap-defaults')).data,
    costImport: async (data) => (await axiosInstance.post('/finance/cost-import', data)).data,
    listCodedCosts: async (period) => (await axiosInstance.get('/finance/cost-imports', { params: period ? { period } : {} })).data,
    deleteCostImport: async (batchId) => (await axiosInstance.delete(`/finance/cost-import/${batchId}`)).data,
    projectValues: async () => (await axiosInstance.get('/finance/project-values')).data,
    saveProjectCoding: async (projectId, data) => (await axiosInstance.patch(`/finance/project-coding/${projectId}`, data)).data,
    workerTimesheets: async (data) => (await axiosInstance.post('/finance/worker-timesheets', data)).data,
    timesheetDecision: async (ids, decision) => (await axiosInstance.post('/finance/timesheets/decision', { ids, decision })).data,
    workflowStatus: async (period) => (await axiosInstance.get('/finance/workflow-status', { params: { period } })).data,
    projectLabourTotals: async (period) => (await axiosInstance.get('/finance/project-labour-totals', { params: { period } })).data,
    auditLog: async (params = {}) => (await axiosInstance.get('/finance/audit-log', { params })).data,
  },
  // Month-end allocation engine + profitability (Finance-only)
  allocation: {
    createRun: async (period) => (await axiosInstance.post('/allocation/runs', { period })).data,
    listRuns: async () => (await axiosInstance.get('/allocation/runs')).data,
    getRun: async (id) => (await axiosInstance.get(`/allocation/runs/${id}`)).data,
    compute: async (id, stage) => (await axiosInstance.post(`/allocation/runs/${id}/compute`, { stage })).data,
    lockRun: async (id) => (await axiosInstance.post(`/allocation/runs/${id}/lock`)).data,
    unlockRun: async (id, reason) => (await axiosInstance.post(`/allocation/runs/${id}/unlock`, { reason })).data,
    journal: async (id) => (await axiosInstance.get(`/allocation/runs/${id}/journal`)).data,
    profitability: async (period) => (await axiosInstance.get('/allocation/profitability', { params: { period } })).data,
    listAdjustments: async (period) => (await axiosInstance.get('/allocation/revenue-adjustments', { params: period ? { period } : {} })).data,
    profitabilityLines: async (period, projectId, component) => (await axiosInstance.get('/allocation/profitability-lines', { params: { period, projectId, component } })).data,
    createAdjustment: async (data) => (await axiosInstance.post('/allocation/revenue-adjustments', data)).data,
    deleteAdjustment: async (id) => (await axiosInstance.delete(`/allocation/revenue-adjustments/${id}`)).data,
  },
  // Accounts Payable (native vendors, POs, endorsements, invoices, payments)
  ap: {
    listVendors: async () => (await axiosInstance.get('/ap/vendors')).data,
    createVendor: async (data) => (await axiosInstance.post('/ap/vendors', data)).data,
    updateVendor: async (id, data) => (await axiosInstance.patch(`/ap/vendors/${id}`, data)).data,
    listPos: async () => (await axiosInstance.get('/ap/purchase-orders')).data,
    poLines: async (id) => (await axiosInstance.get(`/ap/purchase-orders/${id}/lines`)).data,
    createPo: async (data) => (await axiosInstance.post('/ap/purchase-orders', data)).data,
    issuePo: async (id) => (await axiosInstance.post(`/ap/purchase-orders/${id}/issue`)).data,
    closePo: async (id, cancelResidual) => (await axiosInstance.post(`/ap/purchase-orders/${id}/close`, { cancelResidual })).data,
    cancelPo: async (id, reason) => (await axiosInstance.post(`/ap/purchase-orders/${id}/cancel`, { reason })).data,
    createEndorsement: async (data) => (await axiosInstance.post('/ap/endorsements', data)).data,
    listInvoices: async () => (await axiosInstance.get('/ap/invoices')).data,
    createInvoice: async (data) => (await axiosInstance.post('/ap/invoices', data)).data,
    listPaymentRequests: async () => (await axiosInstance.get('/ap/payment-requests')).data,
    createPaymentRequest: async (data) => (await axiosInstance.post('/ap/payment-requests', data)).data,
    paymentDecision: async (id, decision, note) => (await axiosInstance.post(`/ap/payment-requests/${id}/decision`, { decision, note })).data,
    updatePaymentRequest: async (id, data) => (await axiosInstance.patch(`/ap/payment-requests/${id}`, data)).data,
    executePayment: async (id, data) => (await axiosInstance.post(`/ap/payment-requests/${id}/execute`, data)).data,
    listAdvances: async () => (await axiosInstance.get('/ap/advances')).data,
    allocateAdvance: async (invoiceId, advanceRequestId, amount) => (await axiosInstance.post(`/ap/invoices/${invoiceId}/allocate-advance`, { advanceRequestId, amount })).data,
    listCreditNotes: async () => (await axiosInstance.get('/ap/credit-notes')).data,
    createCreditNote: async (data) => (await axiosInstance.post('/ap/credit-notes', data)).data,
    rematchInvoice: async (id, justification) => (await axiosInstance.post(`/ap/invoices/${id}/rematch`, { justification })).data,
    amendPo: async (id, data) => (await axiosInstance.post(`/ap/purchase-orders/${id}/amend`, data)).data,
    poVersions: async (id) => (await axiosInstance.get(`/ap/purchase-orders/${id}/versions`)).data,
    unreconciled: async () => (await axiosInstance.get('/ap/executions/unreconciled')).data,
    reconcile: async (id, bankStatementRef) => (await axiosInstance.post(`/ap/executions/${id}/reconcile`, { bankStatementRef })).data,
    zohoStatus: async () => (await axiosInstance.get('/ap/zoho-status')).data,
    getSettings: async () => (await axiosInstance.get('/ap/settings')).data,
    saveSettings: async (data) => (await axiosInstance.post('/ap/settings', data)).data,
    writeOff: async (invoiceId, amount, reason) => (await axiosInstance.post(`/ap/invoices/${invoiceId}/write-off`, { amount, reason })).data,
    importZohoVendors: async () => (await axiosInstance.post('/ap/vendors/import-zoho')).data,
  },
  // Security of Payment Act tracking on progress claims
  claims: {
    sopRegister: async (params = {}) => (await axiosInstance.get('/claims/sop/register', { params })).data,
    getSop: async (id) => (await axiosInstance.get(`/claims/${id}/sop`)).data,
    setServed: async (id, data) => (await axiosInstance.put(`/claims/${id}/sop/served`, data)).data,
    setResponse: async (id, data) => (await axiosInstance.put(`/claims/${id}/sop/response`, data)).data,
    setOutcome: async (id, outcome) => (await axiosInstance.put(`/claims/${id}/sop/outcome`, { outcome })).data,
  },
  // Project page commercial card
  projects: {
    financialSummary: async (id) => (await axiosInstance.get(`/projects/${id}/financial-summary`)).data,
  },
  // Variation order and back-charge registers
  variations: {
    summary: async (params = {}) => (await axiosInstance.get('/variations/summary', { params })).data,
    listVos: async (params = {}) => (await axiosInstance.get('/variations/vos', { params })).data,
    createVo: async (data) => (await axiosInstance.post('/variations/vos', data)).data,
    updateVo: async (id, data) => (await axiosInstance.patch(`/variations/vos/${id}`, data)).data,
    decideVo: async (id, data) => (await axiosInstance.post(`/variations/vos/${id}/decision`, data)).data,
    deleteVo: async (id) => (await axiosInstance.delete(`/variations/vos/${id}`)).data,
    listBackCharges: async (params = {}) => (await axiosInstance.get('/variations/back-charges', { params })).data,
    createBackCharge: async (data) => (await axiosInstance.post('/variations/back-charges', data)).data,
    updateBackCharge: async (id, data) => (await axiosInstance.patch(`/variations/back-charges/${id}`, data)).data,
    deleteBackCharge: async (id) => (await axiosInstance.delete(`/variations/back-charges/${id}`)).data,
  },
  // Zoho read-only proxy (PO / invoice / payment data linked by PR code)
  zoho: {
    status: async () => (await axiosInstance.get('/zoho/status')).data,
    posByPR: async (prCode) => (await axiosInstance.get(`/zoho/po/by-pr/${encodeURIComponent(prCode)}`)).data,
    po: async (id) => (await axiosInstance.get(`/zoho/po/${id}`)).data,
    invoices: async (poId) => (await axiosInstance.get(`/zoho/po/${poId}/invoices`)).data,
    payments: async (poId) => (await axiosInstance.get(`/zoho/po/${poId}/payments`)).data,
    vendor: async (id) => (await axiosInstance.get(`/zoho/vendor/${id}`)).data,
    vendors: async () => (await axiosInstance.get('/zoho/vendors')).data,
  },
  // Generic HTTP methods for custom endpoints
  post: async (endpoint, data) => {
    const response = await axiosInstance.post(endpoint, data);
    return response.data;
  },
  get: async (endpoint) => {
    const response = await axiosInstance.get(endpoint);
    return response.data;
  }
};

export default api;
